import { clsx } from '@mantine/styles'
import dayjs from 'dayjs'
import { memo, useMemo } from 'react'

import { Box, FormField, Select } from '@percent/lemonade'
import { TimePicker, DatePicker } from '@percent/domain/giving'

import { OpportunityType } from '../../../dtos/opportunity.dto'
import type { OpportunityFormDateFieldsProps } from './opportunity-form-date-fields.types'
import styles from './opportunity-form-date-fields.module.scss'

const ReadonlyStartDateDateField = memo(function ReadonlyStartDateDateField({
  startDateLabel,
  opportunityType,
  previousStartDate,
  formatDate,
  datePlaceholder,
  colorTheme,
  endDateLabel,
  onBlur,
  dateValue,
  dateError,
  dateTouched,
  setDateTouched,
  onDateChange,
}: Pick<
  OpportunityFormDateFieldsProps,
  | 'startDateLabel'
  | 'endDateLabel'
  | 'opportunityType'
  | 'previousStartDate'
  | 'formatDate'
  | 'datePlaceholder'
  | 'colorTheme'
  | 'onBlur'
  | 'dateValue'
  | 'dateError'
  | 'dateTouched'
  | 'setDateTouched'
  | 'onDateChange'
>) {
  return (
    <>
      <FormField
        label={startDateLabel}
        necessity={
          opportunityType === OpportunityType.Event ? 'required' : 'optional'
        }
        data-testid="startDateField"
        disabled
      >
        <DatePicker
          disabled
          placeholder=""
          error={false}
          handleChange={() => {
            /* empty */
          }}
          handleBlur={onBlur}
          fieldName="startDate"
          value={previousStartDate}
          formatDate={formatDate}
          colorTheme={colorTheme}
        />
      </FormField>
      <FormField
        label={endDateLabel}
        necessity={
          opportunityType === OpportunityType.Event ? 'required' : 'optional'
        }
        status={dateTouched && dateError ? 'danger' : 'default'}
        statusMessage={dateError}
        data-testid="dateField"
      >
        <DatePicker
          fieldName="date"
          placeholder={datePlaceholder}
          value={dateValue?.[1]}
          minDate={dayjs(previousStartDate).toDate()}
          handleBlur={onBlur}
          handleChange={(_date) => {
            const date = Array.isArray(_date) ? _date[1] : _date
            setDateTouched()
            onDateChange([previousStartDate, date])
          }}
          error={!!dateError && !!dateTouched}
          formatDate={formatDate}
          colorTheme={colorTheme}
        />
      </FormField>
    </>
  )
})

const ReadonlyDateFields = memo(function ReadonlyDateFields({
  startDateLabel,
  endDateLabel,
  opportunityType,
  dateValue,
  formatDate,
  colorTheme,
  onBlur,
}: Pick<
  OpportunityFormDateFieldsProps,
  | 'startDateLabel'
  | 'endDateLabel'
  | 'opportunityType'
  | 'dateValue'
  | 'formatDate'
  | 'colorTheme'
  | 'onBlur'
>) {
  return (
    <>
      <FormField
        label={startDateLabel}
        necessity={
          opportunityType === OpportunityType.Event ? 'required' : 'optional'
        }
        data-testid="startDateField"
        disabled
      >
        <DatePicker
          disabled
          placeholder=""
          error={false}
          handleChange={() => {
            /* empty */
          }}
          handleBlur={onBlur}
          fieldName="startDate"
          value={dateValue?.[0] ?? undefined}
          formatDate={formatDate}
          colorTheme={colorTheme}
        />
      </FormField>
      <FormField
        label={endDateLabel}
        necessity={
          opportunityType === OpportunityType.Event ? 'required' : 'optional'
        }
        data-testid="endDateField"
        disabled
      >
        <DatePicker
          disabled
          placeholder=""
          error={false}
          handleChange={() => {
            /* empty */
          }}
          handleBlur={onBlur}
          fieldName="endDate"
          value={dateValue?.[1] ?? undefined}
          formatDate={formatDate}
          colorTheme={colorTheme}
        />
      </FormField>
    </>
  )
})

const DateField = memo(function DateField({
  dateLabel,
  opportunityType,
  dateValue,
  dateError,
  dateTouched,
  setDateTouched,
  onDateChange,
  formatDate,
  datePlaceholder,
  onBlur,
  colorTheme,
  datePickerZIndex,
}: Pick<
  OpportunityFormDateFieldsProps,
  | 'dateLabel'
  | 'opportunityType'
  | 'dateValue'
  | 'dateError'
  | 'dateTouched'
  | 'setDateTouched'
  | 'onDateChange'
  | 'formatDate'
  | 'datePlaceholder'
  | 'onBlur'
  | 'colorTheme'
  | 'datePickerZIndex'
>) {
  return (
    <FormField
      label={dateLabel}
      necessity={
        opportunityType === OpportunityType.Event ? 'required' : 'optional'
      }
      status={dateTouched && dateError ? 'danger' : 'default'}
      statusMessage={dateError}
      data-testid="dateField"
    >
      <DatePicker
        rangePicker
        fieldName="date"
        placeholder={datePlaceholder}
        value={dateValue}
        minDate={dayjs().toDate()}
        handleBlur={onBlur}
        handleChange={(date) => {
          setDateTouched()
          const startDate = Array.isArray(date) ? date[0] : date
          const endDate = Array.isArray(date) ? date[1] : date
          onDateChange([startDate, endDate])
        }}
        error={!!dateError && !!dateTouched}
        formatDate={formatDate}
        colorTheme={colorTheme}
        zIndex={datePickerZIndex}
        handleFieldClear={
          opportunityType === OpportunityType.Project
            ? () => {
                onDateChange([undefined, undefined])
              }
            : undefined
        }
      />
    </FormField>
  )
})

export function OpportunityFormDateFields({
  dateValue,
  startTimeValue,
  onStartTimeChange,
  onEndTimeChange,
  dateError,
  startTimeError,
  endTimeError,
  dateTouched,
  startTimeTouched,
  endTimeTouched,
  setStartTimeTouched,
  setEndTimeTouched,
  setDateTouched,
  onDateChange,
  onBlur,
  opportunityType,
  previousStartDate,
  timeZoneMessage,
  colorTheme,
  formatDate,
  datePlaceholder,
  startDateLabel,
  endDateLabel,
  dateLabel,
  startTimeLabel,
  endTimeLabel,
  endTimeValue,
  inputPlaceholder,
  forceReadOnly,
  timeZoneValue,
  timeZoneLabel,
  timeZonePlaceholder,
  timeZoneError,
  timeZoneTouched,
  onTimeZoneChange,
  timeZoneOptions,
  timeZoneNecessity = 'required',
  datePickerZIndex,
}: Readonly<OpportunityFormDateFieldsProps>) {
  const opportunityAlreadyStarted = useMemo(
    () => !!previousStartDate && dayjs(previousStartDate).isBefore(dayjs()),
    [previousStartDate]
  )

  const isReadOnly = forceReadOnly || opportunityAlreadyStarted

  const timeFieldsNecessity = useMemo(() => {
    if (opportunityType === OpportunityType.Event) {
      return 'required'
    }

    if (dateValue?.some((date) => !!date)) {
      return 'required'
    }

    return 'optional'
  }, [opportunityType, dateValue])

  return (
    <>
      <Box
        className={clsx(styles.DateFieldWrapper, {
          [styles.TwoFields]: isReadOnly,
        })}
      >
        {forceReadOnly && (
          <ReadonlyDateFields
            startDateLabel={startDateLabel}
            endDateLabel={endDateLabel}
            opportunityType={opportunityType}
            dateValue={dateValue}
            formatDate={formatDate}
            colorTheme={colorTheme}
            onBlur={onBlur}
          />
        )}
        {!forceReadOnly && opportunityAlreadyStarted && (
          <ReadonlyStartDateDateField
            startDateLabel={startDateLabel}
            opportunityType={opportunityType}
            previousStartDate={previousStartDate}
            formatDate={formatDate}
            datePlaceholder={datePlaceholder}
            colorTheme={colorTheme}
            endDateLabel={endDateLabel}
            onBlur={onBlur}
            dateValue={dateValue}
            dateError={dateError}
            dateTouched={dateTouched}
            setDateTouched={setDateTouched}
            onDateChange={onDateChange}
          />
        )}
        {!forceReadOnly && !opportunityAlreadyStarted && (
          <DateField
            dateLabel={dateLabel}
            opportunityType={opportunityType}
            dateValue={dateValue}
            dateError={dateError}
            dateTouched={dateTouched}
            setDateTouched={setDateTouched}
            onDateChange={onDateChange}
            formatDate={formatDate}
            datePlaceholder={datePlaceholder}
            onBlur={onBlur}
            colorTheme={colorTheme}
            datePickerZIndex={datePickerZIndex}
          />
        )}
      </Box>
      <Box
        className={clsx(styles.FlexWrapper, {
          [styles.ThreeFields]: !!timeZoneOptions,
        })}
      >
        <FormField
          label={startTimeLabel}
          necessity={timeFieldsNecessity}
          status={startTimeTouched && startTimeError ? 'danger' : 'default'}
          statusMessage={startTimeError}
          data-testid="startTimeField"
          description={
            dateValue?.[0] ? `${formatDate(dateValue[0])} ` : undefined
          }
          disabled={isReadOnly}
        >
          <TimePicker
            fieldName="startTime"
            value={startTimeValue}
            handleChange={(value) => {
              setStartTimeTouched()
              onStartTimeChange(value)
            }}
            handleBlur={onBlur}
            error={Boolean(startTimeTouched && startTimeError)}
            handleFieldClear={
              opportunityType === OpportunityType.Event
                ? undefined
                : () => {
                    onStartTimeChange('')
                  }
            }
            inputPlaceholder={inputPlaceholder}
          />
        </FormField>
        <FormField
          label={endTimeLabel}
          necessity={timeFieldsNecessity}
          status={endTimeTouched && endTimeError ? 'danger' : 'default'}
          statusMessage={endTimeError}
          data-testid="endTimeField"
          description={
            dateValue?.[1] ? `${formatDate(dateValue[1])} ` : undefined
          }
          disabled={forceReadOnly}
        >
          <TimePicker
            fieldName="endTime"
            value={endTimeValue}
            handleChange={(value) => {
              setEndTimeTouched()
              onEndTimeChange(value)
            }}
            handleBlur={onBlur}
            error={Boolean(endTimeTouched && endTimeError)}
            handleFieldClear={
              opportunityType === OpportunityType.Event
                ? undefined
                : () => onEndTimeChange('')
            }
            inputPlaceholder={inputPlaceholder}
          />
        </FormField>
        {timeZoneOptions && onTimeZoneChange && (
          <FormField
            label={timeZoneLabel ?? 'Timezone'}
            necessity={timeZoneNecessity}
            status={timeZoneTouched && timeZoneError ? 'danger' : 'default'}
            statusMessage={timeZoneError}
            data-testid="timeZoneField"
          >
            <Select
              key={timeZoneValue}
              searchable
              placeholder={timeZonePlaceholder}
              options={timeZoneOptions}
              defaultValue={timeZoneOptions.find(
                (o) => o.value === timeZoneValue
              )}
              onChange={(event) => event && onTimeZoneChange(event.value)}
              status={timeZoneTouched && timeZoneError ? 'danger' : 'default'}
              borderRadiiSize="large"
            />
          </FormField>
        )}
      </Box>
      {timeZoneMessage && !timeZoneOptions && (
        <Box className={styles.TimeZone}>{timeZoneMessage}</Box>
      )}
    </>
  )
}
